import React from "react";
import { connect } from "react-redux";

import {
    deleteExports,
    downloadExports,
    initialiseExports,
} from "../core/state/administration/Exports";
import QueuedExport from "../core/types/system/QueuedExport";
import List from "../core/components/list/List";
import ListItem from "../core/components/list/ListItem";
import { Alignment, Side, Size } from "../core/types/system/Layout";
import GeneralContainer from "../core/components/containers/GeneralContainer";
import Text from "../core/components/text/Text";
import { IAppState } from "../core/state/Store";

import "./Exports.scss";
import MainToolbarContents from "../core/components/toolbar/MainToolbarContents";
import ButtonMenu from "../core/components/buttons/ButtonMenu";
import Button from "../core/components/buttons/Button";
import { addAlert } from "../core/state/system/Alerts";
import { AlertTypes } from "../core/types/system/AlertEntry";
import { unwrappedDispatch } from "../core/foundation/Dispatching";
import { setPageLoading } from "../core/state/system/UIConfiguration";

interface ExportsProps {
    exports: QueuedExport[];

    setPageLoading: (loading: boolean) => void;
    initialiseExports: () => Promise<void>;
    deleteExports: (exports: QueuedExport[]) => Promise<void>;
    downloadExports: (exports: QueuedExport[]) => Promise<void>;
}

interface ExportsState {
    selections: QueuedExport[];
}

class Exports extends React.Component<ExportsProps, ExportsState> {
    state: ExportsState = {
        selections: [],
    };

    async componentDidMount() {
        try {
            this.props.setPageLoading(true);
            await this.props.initialiseExports();
        } finally {
            this.props.setPageLoading(false);
        }
    }

    selectAll() {
        this.setState({
            selections: this.props.exports,
        });
    }
    deselectAll() {
        this.setState({
            selections: [],
        });
    }

    async delete() {
        const result = await addAlert({
            type: AlertTypes.Confirm,
            text:
                "Are you sure you want to delete these exports?<br />" +
                `<ul class="indented chevron"><li>${this.state.selections
                    .map(item => item.title)
                    .join("</li><li>")}</li></ul>`,
            okText: "Yes",
            cancelText: "No",
        });

        if (result.response) {
            try {
                this.props.setPageLoading(true);
                await this.props.deleteExports(this.state.selections);
                this.setState({
                    selections: [],
                });
            } finally {
                this.props.setPageLoading(false);
            }
        }
    }

    async download(exp?: QueuedExport) {
        const exports = exp ? [exp] : this.state.selections;
        if (!exports.every(item => item.complete)) {
            addAlert({
                type: AlertTypes.Alert,
                icon: "error",
                styleColor: "red",
                text:
                    "One or more selected bundles is incomplete.\nPlease " +
                    "wait until all selected bundles are complete before " +
                    "atttempting to download.",
            });
            return;
        }

        try {
            this.props.setPageLoading(true);
            await this.props.downloadExports(exports);
        } finally {
            this.props.setPageLoading(false);
        }
    }

    downloadItems(exp: QueuedExport) {
        exp.exporT_Files.forEach(item => {
            if (item.azureURL) window.open(item.azureURL, "_blank");
        });
    }

    render() {
        return (
            <section>
                <MainToolbarContents>
                    <ButtonMenu
                        icon="description"
                        text="Files"
                        tooltip="File management options">
                        <Button
                            icon="checklist_rtl"
                            text="Select All"
                            onClick={() => this.selectAll()}
                        />
                        <Button
                            icon="event_list"
                            text="Deselect All"
                            onClick={() => this.deselectAll()}
                        />
                        <Button
                            icon="delete"
                            text="Delete Selected Items"
                            disabled={!this.state.selections.length}
                            onClick={() => this.delete()}
                        />
                        <Button
                            icon="file_download"
                            text="Download Selected Items"
                            disabled={!this.state.selections.length}
                            onClick={() => this.download()}
                        />
                    </ButtonMenu>
                </MainToolbarContents>

                {this.props.exports?.length > 0 ? (
                    <GeneralContainer
                        scaling={true}
                        mid={true}
                        max={true}
                        bufferSide={Side.All}
                        clearanceSide={Side.Top}
                        clearanceSize={Size.Large}
                        alignment={Alignment.Center}>
                        <List<QueuedExport>
                            joined={true}
                            multiSelect={true}
                            selections={this.state.selections}
                            selectionChanged={(_, selections) =>
                                this.setState({ selections })
                            }>
                            {this.props.exports.map((exp, index) => (
                                <ListItem<QueuedExport>
                                    key={index}
                                    data={exp}
                                    panel={true}
                                    autoHeight={true}
                                    selectable={true}
                                    bufferSide={Side.All}>
                                    <div className="half flex-column">
                                        <h3>{exp.title}</h3>

                                        <div className="top-buffer-small flex">
                                            <div>
                                                <b>Created On:</b>
                                                {exp.createdOn
                                                    .toDate()
                                                    .toLocaleShort()}
                                                &nbsp; &nbsp;<b>Created By:</b>{" "}
                                                {exp.createdBy}&nbsp; &nbsp;
                                                <b>Number of files:</b>
                                                {exp.exporT_Files.length}&nbsp;
                                            </div>
                                        </div>
                                    </div>

                                    <div className="half text-right flex-column">
                                        {exp.complete ? (
                                            exp.hasErrors ? (
                                                <div className="error">
                                                    Error Exporting
                                                </div>
                                            ) : (
                                                <>
                                                    <div>
                                                        <b>Completed:&nbsp;</b>
                                                        {exp.completedOn
                                                            .toDate()
                                                            .toLocaleShort()}
                                                    </div>

                                                    <div
                                                        className="right"
                                                        style={{
                                                            outline: "none",
                                                        }}
                                                        onClick={() =>
                                                            this.download(exp)
                                                        }
                                                        data-tip="This export is ready for download">
                                                        <i>file_download</i>
                                                    </div>
                                                </>
                                            )
                                        ) : (
                                            <div>Export in progress</div>
                                        )}
                                    </div>
                                </ListItem>
                            ))}
                        </List>
                    </GeneralContainer>
                ) : (
                    <Text clearanceSide={Side.All}>No exports found.</Text>
                )}
            </section>
        );
    }
}

const mapStateToProps = (state: IAppState) => {
    const { exports } = state.db;
    return {
        exports: exports.data,
    };
};

const mapDispatchToProps = dispatch => {
    return {
        setPageLoading: (loading: boolean) => dispatch(setPageLoading(loading)),
        initialiseExports: () => unwrappedDispatch(initialiseExports()),
        deleteExports: (exports: QueuedExport[]) =>
            unwrappedDispatch(deleteExports(exports)),
        downloadExports: (exports: QueuedExport[]) =>
            unwrappedDispatch(downloadExports(exports)),
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(Exports);
